import { OrganizationRole } from "@/types/enums";
import api from './api';

export interface OrganizationMember {
  id: string;
  email: string;
  role: 'Organization_Owner' | 'Organization_Admin' | 'Organization_Member';
  joinedAt: string;
  status: 'active' | 'pending' | 'suspended';
  firstName?: string;
  lastName?: string;
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: 'Organization_Admin' | 'Organization_Member';
  invitedAt: string;
  expiresAt: string;
  invitedBy: string;
  token: string;
}

export interface InviteUserRequest {
  email: string;
  role: 'Organization_Admin' | 'Organization_Member';
}

export interface UpdateRoleRequest {
  role: 'Organization_Owner' | 'Organization_Admin' | 'Organization_Member';
}

export const userManagementService = {
  /**
   * Get organization members
   */
  async getOrganizationMembers(orgSlug: string): Promise<OrganizationMember[]> {
    const response = await api.get(`/organizations/${orgSlug}/members`);
    const memberships = response.data.data || response.data.members || [];
    
    // Transform backend format to frontend format
    return memberships.map((m: any) => {
      let role = m.role;
      // Map backend roles to frontend roles
      if (role === OrganizationRole.OWNER) role = 'Organization_Owner';
      else if (role === OrganizationRole.ADMIN) role = 'Organization_Admin';
      else role = 'Organization_Member'; // USER, MEMBER, or any other role
      
      return {
        id: m.user?.id || m.id,
        email: m.user?.email || m.email,
        role: role as any,
        joinedAt: m.createdAt,
        status: 'active',
        firstName: m.user?.firstName,
        lastName: m.user?.lastName,
      };
    });
  },

  /**
   * Get pending invitations
   */
  async getPendingInvitations(orgSlug: string): Promise<PendingInvitation[]> {
    try {
      const response = await api.get(`/organizations/${orgSlug}/invitations`);
      const invitations = response.data.data || [];
      
      // Transform backend format to frontend format
      return invitations.map((inv: any) => ({
        id: inv.id,
        email: inv.email,
        role: `Organization_${inv.role.charAt(0) + inv.role.slice(1).toLowerCase()}` as any,
        invitedAt: inv.createdAt,
        expiresAt: inv.expiresAt,
        invitedBy: 'Admin',
        token: inv.token,
      }));
    } catch (err: any) {
      // Endpoint not implemented yet, return empty array
      if (err.response?.status === 404) {
        return [];
      }
      throw err;
    }
  },

  /**
   * Invite a user to the organization
   */
  async inviteUser(orgSlug: string, invitation: InviteUserRequest): Promise<{ success: boolean; message?: string }> {
    // Transform frontend role format to backend format
    const backendRole = invitation.role === 'Organization_Admin' ? OrganizationRole.ADMIN : 'MEMBER';
    const response = await api.post(`/organizations/${orgSlug}/invite`, {
      email: invitation.email,
      role: backendRole,
    });
    return response.data;
  },

  /**
   * Cancel a pending invitation
   */
  async cancelInvitation(orgSlug: string, invitationId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/organizations/${orgSlug}/invitations/${invitationId}`);
    return response.data;
  },

  /**
   * Resend an invitation
   */
  async resendInvitation(orgSlug: string, invitationId: string): Promise<{ success: boolean }> {
    const response = await api.post(`/organizations/${orgSlug}/invitations/${invitationId}/resend`);
    return response.data;
  },

  /**
   * Update a member's role
   */
  async updateMemberRole(orgSlug: string, memberId: string, roleUpdate: UpdateRoleRequest): Promise<{ success: boolean }> {
    // Map frontend roles to backend roles
    let backendRole: string;
    if (roleUpdate.role === 'Organization_Owner') backendRole = OrganizationRole.OWNER;
    else if (roleUpdate.role === 'Organization_Admin') backendRole = OrganizationRole.ADMIN;
    else backendRole = 'MEMBER'; // Organization_Member
    
    const response = await api.put(`/organizations/${orgSlug}/members/${memberId}/role`, { role: backendRole });
    return response.data;
  },

  /**
   * Remove a member from the organization
   */
  async removeMember(orgSlug: string, memberId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/organizations/${orgSlug}/members/${memberId}`);
    return response.data;
  },

  /**
   * Transfer organization ownership
   */
  async transferOwnership(orgSlug: string, newOwnerId: string): Promise<{ success: boolean }> {
    const response = await api.post(`/organizations/${orgSlug}/transfer-ownership`, {
      newOwnerId,
    });
    return response.data;
  },

  /**
   * Suspend a member
   */
  async suspendMember(organizationId: string, memberId: string): Promise<{ success: boolean }> {
    const response = await api.post(`/organizations/${organizationId}/members/${memberId}/suspend`);
    return response.data;
  },

  /**
   * Reactivate a suspended member
   */
  async reactivateMember(organizationId: string, memberId: string): Promise<{ success: boolean }> {
    const response = await api.post(`/organizations/${organizationId}/members/${memberId}/reactivate`);
    return response.data;
  },

  /**
   * Get invitation link for sharing
   */
  async getInvitationLink(organizationId: string, invitationId: string): Promise<{ invitationUrl: string }> {
    const response = await api.get(`/organizations/${organizationId}/invitations/${invitationId}/link`);
    return response.data;
  },

  /**
   * Accept an invitation (used during signup flow)
   */
  async acceptInvitation(token: string): Promise<{ 
    success: boolean; 
    organization: { 
      id: string; 
      name: string; 
      slug: string; 
    }; 
  }> {
    const response = await api.post('/invitations/accept', { token });
    return response.data;
  },

  /**
   * Get invitation details by token
   */
  async getInvitationDetails(token: string): Promise<{
    organization: { name: string; slug: string };
    role: string;
    invitedBy: string;
    expiresAt: string;
  }> {
    const response = await api.get(`/invitations/${token}`);
    return response.data;
  },
};
